import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { startOfToday, startOfTomorrow } from 'date-fns';
import useOpenLeadModal from 'components/domain/lead/modal/useOpenLeadModal';
import useOpenCustomDomainModal from 'components/domain/customDomains/modal/useOpenCustomDomainModal';
import useFeatures from '../hooks/useFeatures';
import useAppDispatch from '../hooks/useAppDispatch';
import { refreshAppConfig } from '../store/slices/config';
import {
  AppEventType,
  LeadCreatedEventPayload,
  LeadDeletedEventPayload,
  LeadUpdatedEventPayload,
} from '../models/Events';
import useAppEvent from '../hooks/useAppEvent';
import useHandlePremiumDbsEvents from '../pages/agencySettings/dbs/useHandlePremiumDbsEvents';
import { PremiumDbsEvents } from '../pages/agencySettings/dbs/AgencySettingsDbs.types';

type WindowMessagePayload = {
  leadModalOpenRequest?: {
    leadUid?: string;
    leadForRebookingUid?: string;
  };
  premiumDbsEvents?: PremiumDbsEvents;
  redirectToNewLogin?: boolean;
  redirectUrl?: string;
  whiteLabelingSettingsSaved?: boolean;
  customDomainsModalOpenRequest?: boolean;
  customDomainsShouldMigrate?: boolean;
  customDomainsHasSettings?: boolean;
};

const allowedPagesForCustomDomainModal = ['/agency-settings'];

type WindowMessageEvent = { data: WindowMessagePayload };

const WindowMessagesHandler = () => {
  const dispatch = useAppDispatch();
  const { subscribe, unsubscribe } = useAppEvent();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { createLeadModal, updateLeadModal } = useOpenLeadModal();
  const { openCustomDomainModal } = useOpenCustomDomainModal();
  const { allowDnsSecurity } = useFeatures();

  const isCustomDomainModalEnabled =
    allowDnsSecurity &&
    allowedPagesForCustomDomainModal.some((path) => pathname.startsWith(path));
  const handlePremiumDbsEvents = useHandlePremiumDbsEvents();

  // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const postMessageToIframe = (message) => {
    const legacyFrameElement = document.querySelector(
      'iframe#legacy-page-frame',
    );

    if (legacyFrameElement) {
      // @ts-ignore
      legacyFrameElement.contentWindow?.postMessage(message, '*');
    }
  };

  // message listener needs to be memoized in order to have it added only once
  const onMessageReceived = useCallback(
    ({
      data: {
        leadModalOpenRequest,
        redirectUrl,
        redirectToNewLogin,
        whiteLabelingSettingsSaved,
        premiumDbsEvents,
        customDomainsModalOpenRequest,
        customDomainsShouldMigrate,
        customDomainsHasSettings,
      },
    }: WindowMessageEvent) => {
      if (whiteLabelingSettingsSaved) {
        dispatch(refreshAppConfig());
      }

      if (customDomainsModalOpenRequest && isCustomDomainModalEnabled) {
        openCustomDomainModal({
          shouldMigrate: Boolean(customDomainsShouldMigrate),
          hasSettings: Boolean(customDomainsHasSettings),
        });
      }

      if (redirectUrl) {
        navigate(redirectUrl);
      }
      if (redirectToNewLogin) {
        navigate('/logout'); // in case of a login page redirection from pmp-core it must be assured that tokens are removed
      }

      if (leadModalOpenRequest) {
        const { leadUid, leadForRebookingUid } = leadModalOpenRequest;

        if (leadUid) {
          updateLeadModal({ jobCreationNotAllowed: true, leadUid });
        } else {
          createLeadModal({
            dateFrom: startOfToday(),
            dateTo: startOfTomorrow(),
            leadForRebookingUid,
            jobCreationNotAllowed: true,
          });
        }
      }

      if (premiumDbsEvents) {
        handlePremiumDbsEvents(premiumDbsEvents);
      }
    },
    [dispatch, navigate, openCustomDomainModal, isCustomDomainModalEnabled],
  );

  useEffect(() => {
    if (!window.frameElement) {
      window.addEventListener('message', onMessageReceived);

      const leadCreatedListener = ({
        detail: {
          lead: { uid, status },
        },
      }: CustomEvent<LeadCreatedEventPayload>) => {
        postMessageToIframe({
          leadAlterationRequest: {
            createdLeadUid: uid,
            createdLeadStatus: status,
          },
        });
      };
      const leadDeletedListener = ({
        detail: { leadUid },
      }: CustomEvent<LeadDeletedEventPayload>) => {
        postMessageToIframe({
          leadAlterationRequest: { deletedLeadUid: leadUid },
        });
      };
      const leadUpdatedListener = ({
        detail: {
          lead: { uid },
        },
      }: CustomEvent<LeadUpdatedEventPayload>) => {
        postMessageToIframe({ leadAlterationRequest: { updatedLeadUid: uid } });
      };

      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      subscribe(AppEventType.LEAD_CREATED, leadCreatedListener);
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      subscribe(AppEventType.LEAD_DELETED, leadDeletedListener);
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      subscribe(AppEventType.LEAD_UPDATED, leadUpdatedListener);

      return () => {
        window.removeEventListener('message', onMessageReceived);
        // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        unsubscribe(AppEventType.LEAD_CREATED, leadCreatedListener);
        // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        unsubscribe(AppEventType.LEAD_DELETED, leadDeletedListener);
        // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        unsubscribe(AppEventType.LEAD_UPDATED, leadUpdatedListener);
      };
    }

    return () => {};
  }, [onMessageReceived]);

  return null;
};

export default WindowMessagesHandler;
